"use client";

import { toast } from "@dafiti/closet-ui";
import { useTranslations } from "next-intl";
import { useEnvContext } from "next-runtime-env";
import { usePathname } from "next/navigation";
import { useCallback } from "react";

import { fetchCatalogProducts, toFilterItems } from "../api/catalog.query";
import { useCatalogBlockContext } from "../components/CatalogPage/CatalogBlockContext";
import { getCatalogHeaders } from "../server/catalogHeaders.helper";
import { useFiltersStore } from "../stores/filters.store";
import { parseCatalogRoute } from "../types/catalogRoute.helper";
import { buildSearchPathForBff } from "../types/searchTerm.helper";

function resolveEffectivePath({
  pathname,
  pending_category_path,
  isCMS,
  cmsPath,
}: {
  pathname: string;
  pending_category_path?: string;
  isCMS?: boolean;
  cmsPath?: string;
}): string {
  const route = parseCatalogRoute(pathname);

  if (route.isCategoryDrill && pending_category_path) {
    return `${route.basePrefix}${pending_category_path}`;
  }

  return isCMS && cmsPath ? cmsPath : pathname;
}

export function useLiveUpdate() {
  const t = useTranslations("catalog");
  const { NEXT_PUBLIC_BFF_API } = useEnvContext();
  const { isCMS, path } = useCatalogBlockContext();
  const pathname = usePathname();

  const triggerLiveUpdate = useCallback(async () => {
    const { pendingFilters, updateDraft, isLiveUpdateLoading } =
      useFiltersStore.getState();

    if (isLiveUpdateLoading) return;

    const pendingFiltersSnapshot = pendingFilters;

    useFiltersStore.setState({ isLiveUpdateLoading: true });

    const { pending_category_path, ...filtersForBff } = pendingFilters ?? {};
    const filters = toFilterItems(filtersForBff);

    const effectivePath = resolveEffectivePath({
      pathname,
      pending_category_path,
      isCMS,
      cmsPath: path,
    });

    try {
      const response = await fetchCatalogProducts({
        apiOrigin: NEXT_PUBLIC_BFF_API!,
        path: buildSearchPathForBff(effectivePath),
        filters: filters.length > 0 ? filters : undefined,
        extraHeaders: getCatalogHeaders(),
        isCMSPath: isCMS,
      });

      updateDraft(response.facets ?? [], response.pagination.total);
    } catch {
      useFiltersStore.setState({
        isLiveUpdateLoading: false,
        pendingFilters: pendingFiltersSnapshot,
      });

      toast({
        message: t("errors.applyFilters"),
        type: "error",
        icon: true,
        position: "top-right",
      });
    }
  }, [NEXT_PUBLIC_BFF_API, pathname, isCMS, path, t]);

  const isLiveUpdateLoading = useFiltersStore(
    (state) => state.isLiveUpdateLoading,
  );

  return { triggerLiveUpdate, isLiveUpdateLoading };
}
